import React, { useState } from 'react';
import { Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import downArrow from 'assets/icons/chevron-down.svg';
import upArrow from 'assets/icons/chevron-up.svg';
import '../Gainlosstable.css';

const metalsWithCode = [
  { code: 'Au', name: 'Gold' },
  { code: 'Ag', name: 'Silver' },
  { code: 'Pt', name: 'Platinum' },
  { code: 'Pd', name: 'Palladium' },
];

interface GainLossTableProps {
  realisedData: Record<
    string,
    {
      sell_price: number;
      buyback_price: number;
      quantity: number;
    }
  > | null | undefined;
  unRealisedData: Record<
    string,
    {
      totalBuybackAmount: number;
      totalSellAmount: number;
      totalSellQuantity: number;
    }
  > | null | undefined;
  realisedLoading: boolean;
}

// ─── Arrow + Amount cell ──────────────────────────────────────────────────────

interface PLCellProps {
  value: number;
  isPercent?: boolean;
}

const PLCell: React.FC<PLCellProps> = ({ value, isPercent = false }) => {
  const absVal = Math.abs(value);
  const display = isPercent
    ? `${value !== 0 ? absVal.toFixed(2) : 0}%`
    : `₹${absVal.toFixed(2)}`;

  return (
    <div className='gl-pl-cell'>
      {value !== 0 && (
        <img
          src={value > 0 ? upArrow : downArrow}
          className='gl-arrow-icon'
          alt={value > 0 ? 'up' : 'down'}
        />
      )}
      <span className={value > 0 ? 'gl-gain' : value < 0 ? 'gl-loss' : ''}>
        {display}
      </span>
    </div>
  );
};

// ─── Info icon with tooltip ───────────────────────────────────────────────────

interface InfoTooltipProps {
  text: string;
  id: string;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, id }) => (
  <OverlayTrigger
    placement='top'
    overlay={
      <Tooltip id={id} style={{ fontSize: '12px' }}>
        {text}
      </Tooltip>
    }
  >
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '5px',
        width: '15px',
        height: '15px',
        borderRadius: '50%',
        backgroundColor: '#99834d',
        color: '#fff',
        fontSize: '10px',
        fontWeight: 'bold',
        cursor: 'pointer',
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      i
    </span>
  </OverlayTrigger>
);

// ─── Section title with tooltip ───────────────────────────────────────────────

interface SectionTitleProps {
  title: string;
  tooltipText: string;
  tooltipId: string;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ title, tooltipText, tooltipId }) => (
  <div className='gl-section-header'>
    <h5 className='gl-title'>
      {title}
      <OverlayTrigger
        placement='top'
        overlay={
          <Tooltip id={tooltipId} style={{ fontSize: '12px' }}>
            {tooltipText}
          </Tooltip>
        }
      >
        <sup
          style={{
            marginLeft: '3px',
            fontSize: '12px',
            color: '#99834d',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          * Exclusive of GST
        </sup>
      </OverlayTrigger>
    </h5>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const GainLossTable: React.FC<GainLossTableProps> = ({
  realisedData,
  unRealisedData,
  realisedLoading,
}) => {
  if (realisedLoading) {
    return (
      <div className='gl-loading'>
        <span>Loading...</span>
      </div>
    );
  }

  if (!realisedData) return null;

  const investedMetalSum = Object.keys(realisedData).reduce(
    (acc, m) => acc + realisedData[m].sell_price,
    0
  );
  const realisedMetalSum = Object.keys(realisedData).reduce(
    (acc, m) => acc + realisedData[m].buyback_price,
    0
  );
  const realisedPLSum = realisedMetalSum - investedMetalSum;
  const realisedPLSumPer =
    investedMetalSum > 0
      ? ((realisedMetalSum - investedMetalSum) / investedMetalSum) * 100
      : realisedMetalSum > 0
      ? 100
      : 0;

  const markToMarkSum = unRealisedData
    ? Object.keys(unRealisedData).reduce(
        (acc, m) => acc + unRealisedData[m].totalSellAmount,
        0
      )
    : 0;
  const unInvestedSum = unRealisedData
    ? Object.keys(unRealisedData).reduce(
        (acc, m) => acc + unRealisedData[m].totalBuybackAmount,
        0
      )
    : 0;
  const unrealisedPLSum = markToMarkSum - unInvestedSum;
  const unrealisedPLSumPer =
    unInvestedSum > 0
      ? ((markToMarkSum - unInvestedSum) / unInvestedSum) * 100
      : markToMarkSum > 0
      ? 100
      : 0;

  const getMetalName = (code: string) =>
    metalsWithCode.find((m) => m.code === code)?.name ?? code;

  const metalKeys = Object.keys(realisedData);

  return (
    <Row className='gl-wrapper mt-5 mb-3 gx-2'>
      {/* ── BOOKED GAIN / LOSS ── */}
      <Col md={6} className='gl-col' style={{ minWidth: 0 }}>
        <SectionTitle
          title='Booked Gain / Loss'
          tooltipText='All values are exclusive of GST'
          tooltipId='booked-gst-tooltip'
        />

        <div className='gl-table-container'>
          <table className='gl-table'>
            <thead>
              <tr>
                <th>Metal</th>
                {metalKeys.map((code) => (
                  <th key={code}>{getMetalName(code)}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>Invested Metal Value</b></td>
                {metalKeys.map((m) => (
                  <td key={`${m}-sold`} className='gl-num'>
                    ₹{realisedData[m].sell_price.toFixed(2)}
                  </td>
                ))}
                <td className='gl-num'>₹{investedMetalSum.toFixed(2)}</td>
              </tr>

              <tr>
                <td><b>Realised Metal Value</b></td>
                {metalKeys.map((m) => (
                  <td key={`${m}-inv`} className='gl-num'>
                    ₹{realisedData[m].buyback_price.toFixed(2)}
                  </td>
                ))}
                <td className='gl-num'>₹{realisedMetalSum.toFixed(2)}</td>
              </tr>

              <tr>
                <td><b>Metal Transacted</b></td>
                {metalKeys.map((m) => (
                  <td key={`${m}-qty`} className='gl-num'>
                    {realisedData[m].quantity.toFixed(4)} g
                  </td>
                ))}
                <td className='gl-num gl-dash'>—</td>
              </tr>

              <tr className='gl-highlight-row'>
                <td><b>Gain / Loss (INR)</b></td>
                {metalKeys.map((m) => {
                  const pl = realisedData[m].buyback_price - realisedData[m].sell_price;
                  return (
                    <td key={`${m}-pl-inr`} className='gl-num'>
                      <PLCell value={pl} />
                    </td>
                  );
                })}
                <td className='gl-num'>
                  <PLCell value={realisedPLSum} />
                </td>
              </tr>

              <tr className='gl-highlight-row'>
                <td><b>Gain / Loss (%)</b></td>
                {metalKeys.map((m) => {
                  const pct =
                    realisedData[m].sell_price > 0
                      ? ((realisedData[m].buyback_price - realisedData[m].sell_price) /
                          realisedData[m].sell_price) *
                        100
                      : realisedData[m].buyback_price > 0
                      ? 100
                      : 0;
                  return (
                    <td key={`${m}-pl-pct`} className='gl-num'>
                      <PLCell value={pct} isPercent />
                    </td>
                  );
                })}
                <td className='gl-num'>
                  <PLCell value={realisedPLSumPer} isPercent />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Col>

      {/* ── POTENTIAL GAIN / LOSS ── */}
      <Col md={6} className='gl-col mob-table-margin' style={{ minWidth: 0 }}>
        <SectionTitle
          title='Potential Gain / Loss'
          tooltipText='All values are exclusive of GST'
          tooltipId='potential-gst-tooltip'
        />

        <div className='gl-table-container'>
          <table className='gl-table'>
            <thead>
              <tr>
                <th>Metal</th>
                {metalKeys.map((code) => (
                  <th key={code}>{getMetalName(code)}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>Investment Value</b></td>
                {unRealisedData &&
                  metalKeys.map((m) => (
                    <td key={`${m}-buyback`} className='gl-num'>
                      ₹{unRealisedData[m].totalBuybackAmount.toFixed(2)}
                    </td>
                  ))}
                <td className='gl-num'>₹{Math.abs(unInvestedSum).toFixed(2)}</td>
              </tr>

              {/* Mark to Market with info icon */}
              <tr>
                <td>
                  <b style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Mark to Market
                    <InfoTooltip
                      text='Mark to Market Value is the value of your asset as per current sell price in the market'
                      id='mtm-tooltip'
                    />
                  </b>
                </td>
                {unRealisedData &&
                  metalKeys.map((m) => (
                    <td key={`${m}-sell`} className='gl-num'>
                      ₹{unRealisedData[m].totalSellAmount.toFixed(2)}
                    </td>
                  ))}
                <td className='gl-num'>₹{Math.abs(markToMarkSum).toFixed(2)}</td>
              </tr>

              <tr>
                <td><b>Metal Holdings</b></td>
                {unRealisedData &&
                  metalKeys.map((m) => (
                    <td key={`${m}-qty`} className='gl-num'>
                      {unRealisedData[m].totalSellQuantity.toFixed(4)} g
                    </td>
                  ))}
                <td className='gl-num gl-dash'>—</td>
              </tr>

              <tr className='gl-highlight-row'>
                <td><b>Gain / Loss (INR)</b></td>
                {unRealisedData &&
                  metalKeys.map((m) => {
                    const pl =
                      unRealisedData[m].totalSellAmount -
                      unRealisedData[m].totalBuybackAmount;
                    return (
                      <td key={`${m}-pl-inr`} className='gl-num'>
                        <PLCell value={pl} />
                      </td>
                    );
                  })}
                <td className='gl-num'>
                  <PLCell value={unrealisedPLSum} />
                </td>
              </tr>

              <tr className='gl-highlight-row'>
                <td><b>Gain / Loss (%)</b></td>
                {unRealisedData &&
                  metalKeys.map((m) => {
                    const pct =
                      unRealisedData[m].totalBuybackAmount > 0
                        ? ((unRealisedData[m].totalSellAmount -
                            unRealisedData[m].totalBuybackAmount) /
                            unRealisedData[m].totalBuybackAmount) *
                          100
                        : unRealisedData[m].totalSellAmount > 0
                        ? 100
                        : 0;
                    return (
                      <td key={`${m}-pl-pct`} className='gl-num'>
                        <PLCell value={pct} isPercent />
                      </td>
                    );
                  })}
                <td className='gl-num'>
                  <PLCell value={unrealisedPLSumPer} isPercent />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Col>
    </Row>
  );
};

export default GainLossTable;